import { Group, MultiSelect, Select, Stack, Text } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useEffect, useMemo, useState, type FC } from 'react';

import {
    getDistributionChannels,
    getDistributionTrackOverview,
    getDistributionTrackSaleOrders,
    type AvailableChannel,
    type DistributionTrackOverview,
    type DistributionTrackSaleOrder,
    type DistributionTrackSaleOrderStatus,
} from '@api/distribution';
import SectionHeader from '@components/common/SectionHeader';
import DataSheet, { type DataSheetColumn } from '@components/ui/DataSheet';
import StatCard from '@components/ui/StatCard';
import { neutralColors, semanticColors } from '@styles/design-system';
import { formatCount } from '@utils/currency';

interface Props {
    /** Row click → open the sale order's plan, preselected to this shipment code. */
    onOpenSaleOrder: (saleOrder: DistributionTrackSaleOrder) => void;
    /** Bumped by the parent to force a refetch (e.g. after approving/placing a plan). */
    refreshKey?: number;
}

const STATUS_META: Record<
    DistributionTrackSaleOrderStatus,
    { label: string; color: string }
> = {
    NOT_PLACED: { label: 'Approved · not placed', color: semanticColors.warning.primary },
    AWAITING_DISPATCH: { label: 'Awaiting dispatch', color: semanticColors.info.primary },
    DISPATCHED: { label: 'Dispatched', color: semanticColors.positive.primary },
    DEAD: { label: 'Cancelled / returned', color: neutralColors.textDisabled },
    FAILED: { label: 'Push failed', color: semanticColors.critical.primary },
};

const STATUS_OPTIONS = (
    Object.keys(STATUS_META) as DistributionTrackSaleOrderStatus[]
).map((value) => ({ value, label: STATUS_META[value].label }));

function buildColumns(): DataSheetColumn<DistributionTrackSaleOrder>[] {
    return [
        {
            key: 'saleOrderCode',
            header: 'Sale order',
            width: 220,
            align: 'left',
            sticky: true,
            render: (_v, row) => (
                <div style={{ minWidth: 0 }}>
                    <Text style={{ fontSize: 13, fontWeight: 600, color: semanticColors.info.primary }}>
                        {row.saleOrderCode}
                    </Text>
                    <Text style={{ fontSize: 11, color: neutralColors.textMuted, marginTop: 1 }}>
                        {row.channelAbbreviationKey}
                    </Text>
                </div>
            ),
        },
        {
            key: 'planName',
            header: 'Plan',
            width: 220,
            align: 'left',
            render: (_v, row) => (
                <Text style={{ fontSize: 13, color: neutralColors.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {row.planName}
                </Text>
            ),
        },
        {
            key: 'status',
            header: 'Status',
            width: 170,
            align: 'center',
            render: (_v, row) => {
                const meta = STATUS_META[row.status];
                return (
                    <span
                        style={{
                            fontSize: 11,
                            fontWeight: 600,
                            letterSpacing: '0.04em',
                            textTransform: 'uppercase',
                            color: meta.color,
                            backgroundColor: `${meta.color}18`,
                            borderRadius: 4,
                            padding: '3px 8px',
                        }}
                    >
                        {meta.label}
                    </span>
                );
            },
        },
        {
            key: 'wmsExternalId',
            header: 'External code',
            width: 180,
            align: 'left',
            render: (_v, row) => (
                <Text style={{ fontSize: 12, color: row.wmsExternalId ? neutralColors.textSecondary : neutralColors.textMuted }}>
                    {row.wmsExternalId ?? 'Not placed'}
                </Text>
            ),
        },
        {
            key: 'skuCount',
            header: 'SKUs',
            width: 90,
            align: 'right',
            render: (_v, row) => <Text style={{ fontSize: 13 }}>{formatCount(row.skuCount)}</Text>,
        },
        {
            key: 'unitsApproved',
            header: 'Units approved',
            width: 140,
            align: 'right',
            render: (_v, row) => <Text style={{ fontSize: 13 }}>{formatCount(row.unitsApproved)}</Text>,
        },
        {
            key: 'unitsDispatched',
            header: 'Units dispatched',
            width: 150,
            align: 'right',
            render: (_v, row) => {
                const full = row.unitsApproved > 0 && row.unitsDispatched >= row.unitsApproved;
                return (
                    <Text
                        style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: full ? semanticColors.positive.primary : neutralColors.textPrimary,
                        }}
                    >
                        {formatCount(row.unitsDispatched)}
                    </Text>
                );
            },
        },
    ];
}

const TrackView: FC<Props> = ({ onOpenSaleOrder, refreshKey = 0 }) => {
    const [overview, setOverview] = useState<DistributionTrackOverview | null>(null);
    const [saleOrders, setSaleOrders] = useState<DistributionTrackSaleOrder[]>([]);
    const [channels, setChannels] = useState<AvailableChannel[]>([]);
    const [loading, setLoading] = useState(true);

    const [channelFilter, setChannelFilter] = useState<string | null>(null);
    const [statusFilters, setStatusFilters] = useState<DistributionTrackSaleOrderStatus[]>([]);
    // Server-side: a row is a sale order and carries only a SKU count, so the SKU match runs in SQL.
    const [search, setSearch] = useState('');
    const [debouncedSearch] = useDebouncedValue(search, 300);

    // Overview + channel list load once (channel list is filter-independent).
    useEffect(() => {
        Promise.all([
            getDistributionTrackOverview().catch(() => null),
            getDistributionChannels().catch(() => [] as AvailableChannel[]),
        ]).then(([o, ch]) => {
            setOverview(o);
            setChannels(ch);
        });
    }, [refreshKey]);

    // Sale orders refetch whenever the filters change (server-side channel/status scoping).
    useEffect(() => {
        setLoading(true);
        getDistributionTrackSaleOrders({
            channelKey: channelFilter ?? undefined,
            statuses: statusFilters.length > 0 ? statusFilters : undefined,
            search: debouncedSearch || undefined,
        })
            .then((rows) => setSaleOrders(rows))
            .catch(() => setSaleOrders([]))
            .finally(() => setLoading(false));
    }, [refreshKey, channelFilter, statusFilters, debouncedSearch]);

    const channelOptions = useMemo(
        () =>
            channels.map((c) => ({
                value: c.channelAbbreviationKey,
                label: c.channelName || c.channelAbbreviationKey,
            })),
        [channels],
    );

    const dash = (n: number | undefined) => (overview === null ? '—' : String(n ?? 0));

    return (
        <Stack gap={24}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
                <StatCard
                    label="Approved · not placed"
                    value={dash(overview?.notPlaced)}
                    helper="no external sale order yet"
                    valueColor={(overview?.notPlaced ?? 0) > 0 ? semanticColors.warning.primary : undefined}
                    loading={overview === null}
                />
                <StatCard
                    label="Awaiting dispatch"
                    value={dash(overview?.awaitingDispatch)}
                    helper="placed externally, not shipped"
                    loading={overview === null}
                />
                <StatCard
                    label="Ships today"
                    value={dash(overview?.shipsToday)}
                    helper="expected ship date (SO + M2) is today"
                    loading={overview === null}
                />
                <StatCard
                    label="Overdue to ship"
                    value={dash(overview?.overdue)}
                    helper="expected ship date has passed"
                    valueColor={(overview?.overdue ?? 0) > 0 ? semanticColors.critical.primary : undefined}
                    loading={overview === null}
                />
            </div>

            <div>
                <Group justify="space-between" align="center" mb={12}>
                    <SectionHeader title="Sale Orders" />
                    <Group gap={8}>
                        <Select
                            size="xs"
                            placeholder="All channels"
                            clearable
                            data={channelOptions}
                            value={channelFilter}
                            onChange={setChannelFilter}
                            style={{ minWidth: 160 }}
                            comboboxProps={{ withinPortal: true }}
                        />
                        <MultiSelect
                            size="xs"
                            placeholder="All statuses"
                            clearable
                            data={STATUS_OPTIONS}
                            value={statusFilters}
                            onChange={(v) => setStatusFilters(v as DistributionTrackSaleOrderStatus[])}
                            style={{ minWidth: 200 }}
                            comboboxProps={{ withinPortal: true }}
                        />
                    </Group>
                </Group>
                {!loading && saleOrders.length === 0 && !debouncedSearch ? (
                    <Text style={{ fontSize: 13, color: neutralColors.textMuted }}>
                        No sale orders match. Approve a plan and place its shipments to track them here.
                    </Text>
                ) : (
                    <DataSheet
                        columns={buildColumns()}
                        data={saleOrders}
                        isLoading={loading}
                        enableSearch
                        searchPlaceholder="Search SKU or sale order code"
                        searchValue={search}
                        onSearchChange={setSearch}
                        maxHeight="auto"
                        getRowKey={(row) => row.saleOrderCode}
                        onRowClick={(row) => onOpenSaleOrder(row)}
                    />
                )}
            </div>
        </Stack>
    );
};

export default TrackView;
